"use client";

import { createFileRoute, Link } from "@tanstack/react-router";
import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { ArrowLeft, CheckCircle2, Loader2 } from "lucide-react";
import { getMyProductCard, submitProductInquiry } from "@/lib/product-cards.functions";
import { formatInquiredAt } from "@/lib/product-cards";

export const Route = createFileRoute("/_cockpit/cockpit/hundert-tage/anfrage/$key")({
  component: ProductInquiryPage,
});

/**
 * Ticket Z12: Anfrage-Seite zu einer Produktkarte.
 *
 * Kunde und Leistung sind vorbelegt und nicht änderbar; der Preis erscheint nur
 * als Angabe. Kein Zahlungsvorgang, keine Preisverhandlung, keine automatische
 * Auftragsbestätigung.
 */
function ProductInquiryPage() {
  const { key } = Route.useParams();
  const fetchCard = useServerFn(getMyProductCard);
  const submit = useServerFn(submitProductInquiry);

  const [message, setMessage] = useState("");
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  const { data: card, isLoading } = useQuery({
    queryKey: ["cockpit", "hundert-tage", "produkt", key],
    queryFn: () => fetchCard({ data: { serviceKey: key } }),
  });

  async function handleSubmit(event: React.FormEvent) {
    event.preventDefault();
    setSending(true);
    setError(null);
    try {
      await submit({ data: { serviceKey: key, message } });
      setDone(true);
    } catch {
      setError("Die Anfrage konnte nicht gesendet werden. Bitte versuchen Sie es später erneut.");
    } finally {
      setSending(false);
    }
  }

  if (isLoading) {
    return (
      <div className="flex items-center gap-2 text-sm text-muted-foreground">
        <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
        Angebot wird geladen …
      </div>
    );
  }

  if (!card) {
    return (
      <div className="space-y-4">
        <p className="text-sm text-muted-foreground">
          Dieses Angebot ist derzeit nicht anfragbar.
        </p>
        <Link
          to="/cockpit/hundert-tage"
          className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
        >
          <ArrowLeft className="h-4 w-4" aria-hidden />
          Zurück zum Modul
        </Link>
      </div>
    );
  }

  if (done) {
    return (
      <div className="max-w-[60ch] space-y-4">
        <h1 className="flex items-center gap-2 font-display text-2xl font-extrabold text-foreground">
          <CheckCircle2 className="h-6 w-6 shrink-0 text-primary" aria-hidden />
          Anfrage ist angekommen
        </h1>
        <p className="text-sm text-muted-foreground">
          Vielen Dank für Ihre unverbindliche Anfrage zu „{card.title}". Ihre Ansprechperson
          meldet sich bei Ihnen. Es entstehen keine Kosten und es kommt kein Auftrag zustande.
        </p>
        <Link
          to="/cockpit/hundert-tage"
          className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
        >
          <ArrowLeft className="h-4 w-4" aria-hidden />
          Zurück zum Modul
        </Link>
      </div>
    );
  }

  const alreadyInquired = Boolean(card.inquiredAt);

  return (
    <div className="max-w-[60ch] space-y-6">
      <Link
        to="/cockpit/hundert-tage"
        className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
      >
        <ArrowLeft className="h-4 w-4" aria-hidden />
        Zurück zum Modul
      </Link>

      <header>
        <p className="text-xs uppercase tracking-wide text-muted-foreground">
          Unverbindliche Anfrage
        </p>
        <h1 className="mt-1 font-display text-2xl font-extrabold text-foreground">
          {card.title}
        </h1>
      </header>

      <div className="rounded-lg border border-border bg-muted/40 p-5">
        <p className="text-sm text-muted-foreground">{card.shortDescription}</p>
        <p className="mt-3 font-display text-sm font-extrabold text-primary">
          {card.priceLabel}
          {card.billingNote ? (
            <span className="ml-1 font-body text-xs font-normal text-muted-foreground">
              {card.billingNote}
            </span>
          ) : null}
        </p>
      </div>

      {alreadyInquired ? (
        <p className="rounded-md border border-border bg-background p-4 text-sm text-muted-foreground">
          Sie haben diese Leistung am {formatInquiredAt(card.inquiredAt!)} angefragt. Ihre
          Ansprechperson meldet sich bei Ihnen.
        </p>
      ) : (
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label
              htmlFor="produkt-nachricht"
              className="font-display text-sm font-extrabold text-foreground"
            >
              Ihre Nachricht (optional)
            </label>
            <textarea
              id="produkt-nachricht"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              maxLength={2000}
              rows={6}
              className="mt-1 w-full rounded-md border border-input bg-background p-3 text-sm"
              placeholder="Woran denken Sie konkret? Gibt es einen Zeitrahmen?"
            />
          </div>

          {error ? <p className="text-sm text-destructive">{error}</p> : null}

          <button
            type="submit"
            disabled={sending}
            className="inline-flex items-center gap-2 rounded-md bg-brand-button px-4 py-2 font-display text-sm font-extrabold text-brand-button-foreground disabled:opacity-60"
          >
            {sending ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden /> : null}
            Anfrage absenden
          </button>

          <p className="text-xs text-muted-foreground">
            Ihre Anfrage ist unverbindlich. Es entsteht kein Auftrag und keine Zahlungspflicht.
          </p>
        </form>
      )}
    </div>
  );
}
